<template>
  <div class="field-group">
    <label
      v-if="label && variant !== 'narrow'"
      :style="{
        visibility: hideLabel ? 'hidden' : 'visible',
      }"
      :for="id"
      class="label-top block mb-2"
    >
      <slot name="label">
        {{ label }}
      </slot>
    </label>
    <div
      class="field"
      :class="{
        'has-icon': !!$slots.icon,
        'has-label': !!label,
        'has-error': !!error,
        disabled,
        filled: !empty,
        [variant]: true,
        unstyled,
      }"
      v-bind="fieldAttrs"
    >
      <slot :id="id" />
      <label
        v-if="label && variant === 'narrow'"
        :for="id"
        class="label-narrow"
        :style="{
          visibility: hideLabel ? 'hidden' : 'visible',
        }"
      >
        {{ label }}
      </label>
      <span v-if="$slots.icon" class="icon">
        <slot name="icon" />
      </span>
      <component :is="icon" v-else-if="icon" class="block px-2 unit" />
    </div>
    <span class="error-msg">{{ error }}</span>
  </div>
</template>

<script lang="ts" setup>
import type { FunctionalComponent } from 'vue';
import type { VueInstance } from '@vueuse/core';

export type Variant = 'default' | 'narrow';

interface Props {
  id?: string;
  label?: string;
  hideLabel?: boolean;
  variant?: Variant;
  icon?: VueInstance | FunctionalComponent | string | object;
  empty?: boolean;
  error?: string;
  unstyled?: boolean;
  fieldAttrs?: object;
  disabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  variant: 'default',
});
</script>
<style lang="scss" scoped>
.field {
  @apply flex items-center;
  @apply relative;
  @apply outline-0 focus-visible:outline-0;
  @apply text-body-regular;
  @apply border-element-neutral;

  border-width: var(--input-border, 1px);
  border-radius: var(--input-border-radius, theme('borderRadius.4'));
  border-top-left-radius: var(--input-border-top-left-radius, theme('borderRadius.4'));
  border-top-right-radius: var(--input-border-top-right-radius, theme('borderRadius.4'));
  border-bottom-left-radius: var(--input-border-bottom-left-radius, theme('borderRadius.4'));
  border-bottom-right-radius: var(--input-border-bottom-right-radius, theme('borderRadius.4'));

  &:focus-within {
    @apply outline-2;

    outline-style: var(--input-outline, solid);
    outline-color: var(--input-outline-color, var(--color-info-blue-100));

    label {
      color: var(--input-active-color, var(--color-info-blue-100));
    }
  }

  &.unstyled {
    @apply border-none;

    &:focus-within {
      @apply outline-none;
    }
  }
}

.has-icon {
  @apply pr-10.5;
}

.disabled {
  background-color: var(--color-table-head-gray-100);
}

.icon {
  @apply absolute right-3.5 top-1/2 -translate-y-1/2;
  @apply text-table-text-gray;
  @apply fill-current stroke-current;
  @apply pointer-events-none;

  min-width: px(14);
  min-height: px(14);
  width: max-content;
  height: max-content;
}

label.label-top {
  @apply text-input;
}

.field label {
  @include absolutePseudo('before') {
    @apply opacity-0;
    width: calc(100% + #{px(20)});
    height: calc(100% + #{px(4)});
    left: px(-10);
    top: px(-2);
    z-index: -1;

    background: var(--input-label-bg, var(--color-background-light-100, white));

    transition: opacity var(--speed-fast) ease;
  }

  @apply whitespace-nowrap;
  @apply pointer-events-none;

  @apply absolute text-neutral;
  @apply left-3.5 top-1/2 -translate-y-1/2 origin-left;

  --input-active-label-offset: #{px(10)};

  transition: transform var(--speed-fast) ease, left var(--speed-fast) ease,
    top var(--speed-fast) ease;
}

.field.has-label {
  &:focus-within .label-narrow,
  &.filled .label-narrow {
    &::before {
      opacity: 1;
    }

    @apply top-0;
    @apply scale-75;
    @apply absolute;

    left: var(--input-active-label-offset, #{px(4)});
  }
}

.field.has-error {
  @apply border-red-120;
  @apply text-red-120;
  --input-outline-color: theme('colors.red.120');
}
.error-msg {
  @apply text-red-120;
  font-size: px(10);
  line-height: 1.4em;
}

:deep(input),
.field > :first-child {
  @apply w-full;
}
</style>
